import styled from '@emotion/styled';
import { Ref } from 'react';
import { ShopTypes } from '~/lib/data-contract';
import { useTranslations } from '~/services/umbraco/hooks';
import { AccordionItem, AccordionItemRef } from '~/shared/components/Accordion';
import { Text } from '~/shared/components/Text';
import { breakpoints } from '~/theme';
import { hazardSymbols } from '../../utils/hazardSymbols';
import { hazardTranslationKeys } from '../../utils/hazardTranslationKeys';

const HazardAccordion = ({
    product,
    ref,
}: {
    product?: ShopTypes.ProductQueryProduct;
    ref?: Ref<AccordionItemRef>;
}) => {
    const { translate } = useTranslations();

    if (!product?.item?.itemHazardSymbols?.length || !product?.item?.multiHazardSentences?.length) {
        return null;
    }

    const { item } = product;

    return (
        <AccordionItem ref={ref} id="hazardsymbols" header={translate('hazard.hazard')}>
            <Wrapper>
                <Symbols>
                    {item?.itemHazardSymbols?.map((symbol) => {
                        const Icon = hazardSymbols[symbol as keyof typeof hazardSymbols];
                        return (
                            <Symbol key={symbol}>
                                <Icon />
                                <HazardText>
                                    {translate(
                                        hazardTranslationKeys[
                                            symbol as keyof typeof hazardTranslationKeys
                                        ],
                                    )}
                                </HazardText>
                            </Symbol>
                        );
                    })}
                </Symbols>
                {item?.multiHazardSentences?.map((sentence, index) => (
                    <div key={index}>
                        <Text variant="display6">{sentence.title}</Text>
                        {sentence.data?.map((row, index) => (
                            <HazardDataRow key={index}>
                                {row.map((item, index) => (
                                    <HazardDataText key={index} variant="paragraph">
                                        {item}
                                    </HazardDataText>
                                ))}
                            </HazardDataRow>
                        ))}
                    </div>
                ))}
            </Wrapper>
        </AccordionItem>
    );
};

export default HazardAccordion;

const Wrapper = styled.div(({ theme }) => ({
    display: 'flex',
    flexDirection: 'column',
    gap: theme.space[5],
    paddingBlock: theme.space[4],
}));

const Symbols = styled.ul(({ theme }) => ({
    display: 'flex',
    gap: theme.space[2],
}));

const Symbol = styled.li(({ theme }) => ({
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: theme.space[1],

    svg: {
        height: theme.space[8],
        width: theme.space[8],
    },
}));

const HazardText = styled.p(({ theme }) => ({
    textAlign: 'center',
    textTransform: 'uppercase',
    fontSize: theme.fontSizes.ant,
    fontWeight: theme.fontWeights.medium,
    letterSpacing: '-0.1px',
    color: theme.colors.black,
}));

const HazardDataRow = styled.ul(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    gap: theme.space[2],
    paddingBlock: theme.space[2],
    borderBottomWidth: 1,
    borderBottomColor: theme.colors.grey40,
    borderBottomStyle: 'solid',
}));

const HazardDataText = styled(Text)(({ theme }) => ({
    fontSize: theme.fontSizes.mouse,
    lineHeight: theme.lineHeights.guinea,
    color: theme.colors.nightBlack,
    letterSpacing: '-0.1px',
    alignSelf: 'center',
    flex: 1,

    [breakpoints.xxs]: {
        gridTemplateColumns: '1fr',
    },

    [breakpoints.md]: {
        lineHeight: theme.lineHeights.coyote,
        letterSpacing: '-0.11px',
    },
}));
